import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { Announcement } from "../../../store/api/announcementsApi";
import { formatDateTime } from "../../../utils/formatDate";

interface AnnouncementTableProps {
	announcements: Announcement[];
	onEditAnnouncement: (announcement: Announcement) => void;
	onDeleteAnnouncement: (announcementId: string) => void;
}

const AnnouncementTable: React.FC<AnnouncementTableProps> = ({
	announcements,
	onEditAnnouncement,
	onDeleteAnnouncement,
}) => {
	const [sortField, setSortField] = useState<string>("created_at");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const sortedAnnouncements = [...announcements].sort((a, b) => {
		let aValue: any = a[sortField as keyof Announcement];
		let bValue: any = b[sortField as keyof Announcement];

		if (sortField === "created_at" || sortField === "publish_date") {
			aValue = new Date(aValue || 0).getTime();
			bValue = new Date(bValue || 0).getTime();
		} else if (typeof aValue === "string") {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}

		if (sortDirection === "asc") {
			return aValue > bValue ? 1 : -1;
		} else {
			return aValue < bValue ? 1 : -1;
		}
	});

	const getPriorityColor = (priority: string) => {
		switch (priority) {
			case "info":
				return "bg-primary/10 text-primary";
			case "warning":
				return "bg-warning/10 text-warning";
			case "success":
				return "bg-success/10 text-success";
			case "urgent":
				return "bg-error/10 text-error";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "active":
				return "bg-success/10 text-success";
			case "draft":
				return "bg-muted text-muted-foreground";
			case "archived":
				return "bg-muted/50 text-muted-foreground";
			default:
				return "bg-muted text-muted-foreground";
		}
	};


	return (
		<div className="bg-card rounded-lg border border-border overflow-hidden">
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted/50 border-b border-border">
						<tr>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("title")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Title</span>
									<Icon
										name={
											sortField === "title" && sortDirection === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								Priority
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								Status
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								Targeting
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								<button
									onClick={() => handleSort("created_at")}
									className="flex items-center space-x-1 hover:text-primary"
								>
									<span>Created</span>
									<Icon
										name={
											sortField === "created_at" && sortDirection === "desc"
												? "ChevronDown"
												: "ChevronUp"
										}
										size={16}
									/>
								</button>
							</th>
							<th className="text-left p-4 font-medium text-xs text-foreground">
								Publish Date
							</th>
							<th className="text-right p-4 font-medium text-xs text-foreground">
								Actions
							</th>
						</tr>
					</thead>
					<tbody>
						{sortedAnnouncements.map((announcement) => (
							<tr
								key={announcement.id}
								className="border-b border-border hover:bg-muted/30 smooth-transition"
							>
								<td className="p-4">
									<div>
										<p className="font-medium text-foreground">
											{announcement.title}
										</p>
										<p className="text-xs text-muted-foreground mt-1 line-clamp-1">
											{announcement.content}
										</p>
									</div>
								</td>
								<td className="p-4">
									<span
										className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getPriorityColor(
											announcement.priority,
										)}`}
									>
										{announcement.priority}
									</span>
								</td>
								<td className="p-4">
									<span
										className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
											announcement.status,
										)}`}
									>
										{announcement.status}
									</span>
								</td>
								<td className="p-4">
									<div className="text-xs text-muted-foreground">
										{announcement.target_roles.length > 0 && (
											<div>Roles: {announcement.target_roles.join(", ")}</div>
										)}
										{announcement.target_property_names.length > 0 && (
											<div>
												Properties: {announcement.target_property_names.length}
											</div>
										)}
										{announcement.target_lease_codes.length > 0 && (
											<div>Leases: {announcement.target_lease_codes.length}</div>
										)}
										{announcement.target_roles.length === 0 &&
											announcement.target_property_ids.length === 0 &&
											announcement.target_lease_ids.length === 0 && (
												<div>All Users</div>
											)}
										{announcement.target_count !== undefined && (
											<div className="mt-1 text-primary font-medium">
												~{announcement.target_count} recipients
											</div>
										)}
									</div>
								</td>
								<td className="p-4 text-sm text-muted-foreground">
									{formatDateTime(announcement.created_at)}
								</td>
								<td className="p-4 text-sm text-muted-foreground">
									{announcement.publish_date
										? formatDateTime(announcement.publish_date)
										: "Immediate"}
								</td>
								<td className="p-4">
									<div className="flex items-center justify-end space-x-2">
										<Button
											variant="ghost"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												onEditAnnouncement(announcement);
											}}
											iconName="Edit"
										/>
										<Button
											variant="ghost"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												onDeleteAnnouncement(announcement.id);
											}}
											iconName="Trash2"
											className="text-destructive hover:text-destructive"
										/>
									</div>
								</td>
							</tr>
						))}
					</tbody>
					</table>
				</div>
			</div>
		</div>
	);
};

export default AnnouncementTable;
